'use client';
import Image from '@/components/Image';
import { useJump } from '@/components/MagicJump';
import Popover from '@/components/Popover';
import { colors, font } from '@/theme/zeelool.theme';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Button } from '@mui/material';
import { CheckOutlined } from '@zeelool/icon';
import { useLocale } from 'next-intl';
import { useTranslations } from '@/i18n/use-translations';
import { useRouter } from 'next/navigation';
import React from 'react';
import { PointsTaskItemProps } from './handlePointsTask';

const buttonWidths = {
  [EPlatform.WEB]: {
    en: '120px',
    de: '170px',
    es: '170px',
    fr: '170px',
    it: '170px',
    pt: '170px',
    fi: '170px',
    nl: '170px',
  },
  [EPlatform.WAP]: {
    en: '50px',
    de: '108px',
    es: '108px',
    fr: '108px',
    it: '108px',
    pt: '108px',
    fi: '108px',
    nl: '108px',
  },
} as const;

const getContentClassName = (isDialog: boolean, deviceType: EPlatform) => {
  if (isDialog && deviceType === EPlatform.WEB) return 'flex flex-col gap-8 w-[65%] ml-16';
  if (isDialog && deviceType === EPlatform.WAP) return 'flex flex-col ml-8 gap-[5px] w-[70%] ml-8';
  if (!isDialog && deviceType === EPlatform.WEB) return 'ml-12';
  if (!isDialog && deviceType === EPlatform.WAP) return 'ml-8';
  return '';
};

const getContainerClassName = (isDialog: boolean, deviceType: EPlatform) => {
  const baseClasses = 'flex items-center';
  if (isDialog && deviceType === EPlatform.WEB)
    return `${baseClasses} bg-auxiliary-gray rounded-12 py-[20px] px-[16px]`;

  if (!isDialog && deviceType === EPlatform.WEB)
    return `${baseClasses} py-12 px-16 rounded-[6px] bg-white`;
  if (!isDialog && deviceType === EPlatform.WAP)
    return `${baseClasses} pt-[17px] pb-[12px] pl-[10px] pr-[10px] rounded-[10px] bg-white`;
  if (isDialog && deviceType === EPlatform.WAP)
    return `${baseClasses} bg-auxiliary-gray pt-[17px] pb-[12px] pl-[10px] pr-[10px] rounded-[10px]`;
  return baseClasses;
};

const getImageClassName = (isDialog: boolean, deviceType: EPlatform) => {
  if (isDialog && deviceType === EPlatform.WEB) return 'w-40 h-40';
  if (deviceType === EPlatform.WAP) return 'w-30 h-30';
  return '';
};

export const TaskItem: React.FC<PointsTaskItemProps & { variant?: 'default' | 'dialog' }> = ({
  item,
  variant = 'default',
  deviceType,
}) => {
  const isDialog = variant === 'dialog';
  const router = useRouter();
  type TaskName =
    | 'app_sign_in'
    | 'buy'
    | 'complete_questionnaire'
    | 'join_fb'
    | 'join_member'
    | 'order_review'
    | 'perfect_info'
    | 'recommend_buy'
    | 'recommend_reg'
    | 'save_prescription'
    | 'verify_email'
    | 'verify_tel';
  // 可通过此方法获取所有任务的翻译
  useTranslations('account.pointsMall.pointsTask.tasks');
  const t = useTranslations(`account.pointsMall.pointsTask.tasks.${item.name as TaskName}`);
  const tTips = useTranslations(`account.pointsMall.pointsTask.tips`);
  const locale = useLocale();
  const toJumpTarget = useJump();
  const getTips = () => {
    const taskTips = {
      buy: () => {
        return tTips('buy');
      },
      save_prescription: (points: string | number) => {
        return tTips('save_prescription', {
          points: `${points} ${Number(points) > 1 ? t('points') : t('point')}`,
        });
      },
      verify_email: (points: string | number) => {
        return tTips('verify_email', {
          points: `${points} ${t('times')} ${Number(points) > 1 ? t('points') : t('point')}`,
        });
      },
      verify_tel: (points: string | number) => {
        return tTips('verify_tel', {
          points: `${points} ${Number(points) > 1 ? t('points') : t('point')}`,
        });
      },
      order_review: (points: string | number) => {
        return tTips('order_review', {
          points: `${points} ${Number(points) > 1 ? t('points') : t('point')}`,
        });
      },
      complete_questionnaire: (points: string | number) => {
        return tTips('complete_questionnaire', {
          points: `${points} ${Number(points) > 1 ? t('points') : t('point')}`,
        });
      },
      recommend_buy: (points: string | number) => {
        return tTips('recommend_buy', {
          points: `${points} ${Number(points) > 1 ? t('points') : t('point')}`,
        });
      },
      recommend_reg: (points: string | number) => {
        return tTips('recommend_reg', {
          points: `${points} ${Number(points) > 1 ? t('points') : t('point')}`,
        });
      },
      join_fb: (points: string | number) => {
        return tTips('join_fb', {
          points: `${points} ${Number(points) > 1 ? t('points') : t('point')}`,
        });
      },
      app_sign_in: () => {
        return tTips('app_sign_in');
      },
      join_member: (points: string | number) => {
        return tTips('join_member', {
          points: `${points} ${Number(points) > 1 ? t('points') : t('point')}`,
        });
      },
      perfect_info: (points: string | number) => {
        return tTips('perfect_info', {
          points: `${points} ${Number(points) > 1 ? t('points') : t('point')}`,
        });
      },
    };
    return taskTips[item.name] ? taskTips[item.name](item.points) : '';
  };

  const getButtonWidth = () => {
    const localeKey = locale as keyof (typeof buttonWidths)[EPlatform.WEB];
    return buttonWidths[deviceType]?.[localeKey] || buttonWidths[deviceType]?.en;
  };

  return (
    <div className={getContainerClassName(isDialog, deviceType)}>
      <Image
        className={getImageClassName(isDialog, deviceType)}
        src={item.label_img}
        alt={item.name}
        width={40}
        height={40}
      />

      <div className={getContentClassName(isDialog, deviceType)}>
        <span className={`${deviceType === EPlatform.WEB ? 'text-16' : 'text-14'} font-bb`}>
          {t('title')}
        </span>
        {isDialog && (
          <span
            className={`${deviceType === EPlatform.WEB ? 'text-14 leading-[20px]' : 'text-12 leading-[18px]'}  text-auxiliary-dark-gray`}
          >
            {getTips()}
          </span>
        )}
      </div>

      {item.name === 'app_sign_in' && (
        <div
          className={`ml-auto flex justify-center items-center ${deviceType === EPlatform.WEB ? 'w-[120px]' : 'w-[50px]'}`}
        >
          <Popover
            id={isDialog ? 'app-sign-in-pop' : 'app-sign-in'}
            width={200}
            content={
              <Image
                src="/account/points-mall/task_img/qrcode.png"
                alt="app sign-in"
                width={200}
                height={200}
              />
            }
            triggerComponent={
              <div
                className={`flex items-center justify-center bg-white rounded-8 ml-auto cursor-pointer ${
                  deviceType === EPlatform.WEB ? 'w-[72px] h-[72px]' : 'w-[50px] h-[50px]'
                }`}
              >
                <Image
                  src="/account/points-mall/task_img/qrcode.png"
                  alt="app sign-in"
                  width={deviceType === EPlatform.WEB ? 60 : 40}
                  height={deviceType === EPlatform.WEB ? 60 : 40}
                />
              </div>
            }
          />
        </div>
      )}

      {item.name !== 'app_sign_in' && item.btn_text && Number(item.status) === 2 && (
        <Button
          variant="outlined"
          sx={{
            borderRadius: deviceType === EPlatform.WEB ? '16px' : '21px',
            height: deviceType === EPlatform.WEB ? '32px' : '24px',
            width: getButtonWidth(),
            minWidth: getButtonWidth(),
            color: colors.primaryText,
            borderColor: colors.auxiliaryDarkGray,
            fontFamily: font.bb,
            fontSize: `
              ${deviceType === EPlatform.WEB && !isDialog ? '16px' : ''}
              ${deviceType === EPlatform.WEB && isDialog ? '14px' : ''}
              ${deviceType === EPlatform.WAP ? '12px' : ''}
            `,
            padding: 0,
            textAlign: 'center',
            ml: 'auto',
            '&:hover': {
              backgroundColor: '#f9f9f9',
              color: colors.primaryBlack,
            },
          }}
          onClick={() => {
            if (item.path.includes('account')) {
              router.push(item.path);
            } else {
              // ZELL-64382 记录从Zeelool Rewards进入商品列表

              item.path.startsWith('/goods-list') &&
                sessionStorage.setItem('zl_goodsListEnterPageScene', '积分商城任务'); //
              toJumpTarget({
                jump_type: item.jump_type,
                Jump_target: item.path,
              });
            }
          }}
        >
          {deviceType === EPlatform.WEB ? t('btnText') : t('btnTextMobile')}
        </Button>
      )}
      {item.name !== 'app_sign_in' && item.btn_text && Number(item.status) === 1 && (
        <div
          className={`ml-auto flex justify-center items-center ${deviceType === EPlatform.WEB ? 'w-[120px]' : 'w-[50px]'}`}
        >
          <div
            className={`flex items-center justify-center  bg-primary-purple rounded-full ${
              deviceType === EPlatform.WEB ? 'w-26 h-26' : 'w-20 h-20'
            }`}
          >
            <CheckOutlined
              className={`text-white ${deviceType === EPlatform.WEB ? 'text-26' : 'text-20'}`}
            />
          </div>
        </div>
      )}
    </div>
  );
};
